import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { toast } from 'sonner';

const TestPaymentForm = ({ clientSecret, amount, currency, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    const card = elements.getElement(CardElement);

    try {
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card, billing_details: { name: 'Stripe Config Test' } },
      });

      if (error) {
        toast.error(`Test payment failed: ${error.message}`);
        setProcessing(false);
      } else if (paymentIntent.status === 'succeeded') {
        onSuccess(paymentIntent.id);
      } else {
        toast.error(`Unexpected status: ${paymentIntent.status}`);
        setProcessing(false);
      }
    } catch (err) {
      console.error('Test payment processing error:', err);
      toast.error('Test payment processing error');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="test-payment-form">
      <div className="bg-amber-50 border border-amber-300 rounded-md p-3 text-sm text-amber-900">
        This will charge a <strong>real {amount.toFixed(2)} {currency}</strong> to verify your Stripe
        configuration. Refund it from your Stripe dashboard afterwards if you wish.
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Card Details</label>
        <div className="p-3 border border-gray-300 rounded-md bg-white">
          <CardElement
            options={{
              hidePostalCode: true,
              style: {
                base: { fontSize: '16px', color: '#424770', '::placeholder': { color: '#aab7c4' } },
                invalid: { color: '#9e2146' },
              },
            }}
          />
        </div>
      </div>

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={processing}
          data-testid="test-payment-cancel-button"
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || processing}
          data-testid="test-payment-submit-button"
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {processing ? 'Processing...' : `Pay ${amount.toFixed(2)} ${currency}`}
        </button>
      </div>
    </form>
  );
};

const TestPaymentModal = ({ isOpen, onClose, apiClient, createEndpoint, title }) => {
  const [loading, setLoading] = useState(true);
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [amount, setAmount] = useState(1.0);
  const [currency, setCurrency] = useState('USD');
  const [error, setError] = useState(null);
  const [succeeded, setSucceeded] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    // Reset state each time the modal opens
    setLoading(true);
    setError(null);
    setSucceeded(false);
    setClientSecret(null);
    setStripePromise(null);

    const init = async () => {
      try {
        const response = await apiClient.post(createEndpoint);
        const { client_secret, publishable_key, amount: amt, currency: cur } = response.data;
        if (!client_secret || !publishable_key) {
          throw new Error('Incomplete payment configuration returned');
        }
        setClientSecret(client_secret);
        setAmount(amt || 1.0);
        setCurrency(cur || 'USD');
        setStripePromise(loadStripe(publishable_key));
        setLoading(false);
      } catch (err) {
        const msg = err.response?.data?.detail || 'Failed to start test payment';
        setError(msg);
        setLoading(false);
      }
    };
    init();
  }, [isOpen, apiClient, createEndpoint]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="test-payment-modal">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">{title || 'Test Stripe Payment'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="test-payment-close-button">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8" data-testid="test-payment-loading">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Preparing test payment...</span>
          </div>
        ) : succeeded ? (
          <div className="text-center py-6" data-testid="test-payment-success">
            <svg className="w-14 h-14 text-green-500 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-green-700 font-semibold mb-2">Stripe configuration works!</p>
            <p className="text-sm text-gray-600 mb-4">
              A {amount.toFixed(2)} {currency} payment was processed successfully. You can refund this
              charge from your Stripe dashboard if you wish.
            </p>
            <button
              onClick={onClose}
              data-testid="test-payment-done-button"
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              Done
            </button>
          </div>
        ) : error ? (
          <div className="text-center py-6" data-testid="test-payment-error">
            <svg className="w-12 h-12 text-red-500 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-red-600 mb-4">{error}</p>
            <button onClick={onClose} className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700">
              Close
            </button>
          </div>
        ) : stripePromise && clientSecret ? (
          <Elements stripe={stripePromise}>
            <TestPaymentForm
              clientSecret={clientSecret}
              amount={amount}
              currency={currency}
              onSuccess={() => { setSucceeded(true); toast.success('Test payment successful!'); }}
              onCancel={onClose}
            />
          </Elements>
        ) : null}
      </div>
    </div>
  );
};

export default TestPaymentModal;
